import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { makeAssetDetailsUrl, paths } from '../../../routes';
import { useDispatch, useSelector } from 'react-redux';
import AssetDetailSection from '../../assets/components/AssetDetailSection';
import CustomButton from '../../../components/CustomButton';
import RowActionsMenu from '../../../components/RowActionsMenu';
import { useGetProductsQuery } from '../../../api/group/groupAdminApiSlice';
import {
    invalidateInventoryReaders,
    useRetireObjectMutation,
} from '../../../api/inventory/inventoryApiSlice';
import { readInventoryFailure } from '../../../api/inventory/inventoryErrors';
import { makeHasPermissionSelector } from '../../../selector/authenticationSelector';
import Permission from '../../../common/permissions';
import { bookablePositionCount, freePositions } from '../stationPositions';
import { readObjectActionFailure } from '../objectActionOutcome';
import type { StationDetailResponse, StationObject } from '../../../api/station/stationApiTypes';
import ConfirmationDialog from '../../../components/ConfirmationDialog';
import {
    setConfirmationDialogContent,
    setIsConfirmationDialog,
} from '../../../store/slices/globalSlice';
import AddObjectsDialog from './AddObjectsDialog';
import ChangeProductBar from './ChangeProductBar';

interface Props {
    station: StationDetailResponse;
}

/** One row of the position list: an object, or a position with nothing in it. */
interface PositionRow {
    door: number | null;
    object: StationObject | null;
}

interface HouseRoomListProps {
    rooms: StationObject[];
    productName: (id: string | null) => string | null;
}

/**
 * A house's rooms: name and product, nothing about positions.
 *
 * Read-only on purpose. A room's product is changed on the room's own page
 * (`updateGroupProduct`); the inventory re-link this station page uses for
 * compartments refuses a room with 409 `room_relink`, so offering selection or
 * "change product" here would be a control that can only fail. Each name links
 * to the room, which is where that edit lives.
 */
const HouseRoomList = ({ rooms, productName }: HouseRoomListProps) => {
    const { t } = useTranslation();
    if (rooms.length === 0) return null;
    return (
        <div className={'overflow-x-auto'}>
            <table className={'w-full text-sm'}>
                <thead>
                    <tr className={'border-b border-foreground/5 text-left'}>
                        <th className={'py-2 pr-4 font-medium text-foreground/60'}>
                            {t('station_detail.objects.column_room')}
                        </th>
                        <th className={'py-2 pr-4 font-medium text-foreground/60'}>
                            {t('station_detail.objects.column_product')}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {rooms.map((room) => (
                        <tr
                            key={room.id}
                            className={'border-b border-foreground/5'}
                        >
                            <td className={'py-2 pr-4'}>
                                <Link
                                    to={makeAssetDetailsUrl(room.id)}
                                    className={'text-primary hover:underline'}
                                >
                                    {room.name ?? t('station_detail.objects.unnamed')}
                                </Link>
                            </td>
                            <td className={'py-2 pr-4 text-foreground'}>
                                {productName(room.productId)
                                    ?? t('station_detail.objects.unlinked')}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

/**
 * The station as a list of positions — and the entry point for adding more.
 *
 * ── Why the whole cabinet, not just the objects ────────────────────────────
 *
 * The operator's question here is *"how is this station configured?"* — 10× L,
 * 5× M, 15× S — and a list of only the filled positions cannot answer it: it
 * shows what exists and stays silent about the three empty compartments and the
 * held-back tail. So every bookable position gets a row, filled or not. This is
 * the first, plainest version of the locker plan; a graphical one is a later
 * step, not a different answer.
 *
 * ── Why the product column is the point ────────────────────────────────────
 *
 * A station may hold several Products — that is the schema (`asset.group_id`
 * and `asset.depot_id` are independent; `product_resource_link` is a junction)
 * and it always was. Only the creation wizard was one-product-per-pass (#2280).
 * So the product per position is the fact the whole screen exists to show, and
 * the summary above the table is it aggregated.
 */
const StationObjectsSection = ({ station }: Props) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [adding, setAdding] = useState(false);
    /*
     * Which compartments the operator has ticked, by object id.
     *
     * Kept as ids rather than object references so it survives the refetch a
     * re-link triggers — holding the objects themselves would pin a stale copy
     * of exactly the rows that just changed.
     */
    const [selectedIds, setSelectedIds] = useState<string[]>([]);

    // The UNFILTERED catalogue: this resolves names for products already linked
    // here, which may include ones the add-picker would not offer (a use case
    // since switched off). Filtering the lookup would render "n/a" for a product
    // that plainly exists — a failure looking like an absence.
    const { data: products } = useGetProductsQuery();
    const productName = useMemo(() => {
        const byId = new Map((products ?? []).map((product) => [product.id, product.name]));
        return (id: string | null) => (id ? byId.get(id) ?? id : null);
    }, [products]);

    const objects = station.objects ?? [];
    const selectedObjects = useMemo(
        () => objects.filter((object) => selectedIds.includes(object.id)),
        [objects, selectedIds],
    );
    const toggleSelected = (id: string) => {
        setSelectedIds((current) => (current.includes(id)
            ? current.filter((entry) => entry !== id)
            : [...current, id]));
    };

    /*
     * Retiring a compartment, from the screen the operator is actually on.
     *
     * The capability is not new — `POST /inventory/objects/:assetId/retire`
     * archives the object and frees its position, keeping `doormapping` so a
     * past booking can still be traced to the unit that served it. What was
     * missing is that it only existed on the PRODUCT page, so from the station
     * an object could be added and re-linked but not removed, and "I cannot
     * delete an object" was the correct reading of the screen (PO, 2026-09-12).
     *
     * Retire, not delete: the hard delete (`assetController.deleteAsset`) is
     * master-only and refuses any object ever booked or rented (#2636).
     */
    const hasDeletePermission = useSelector(
        makeHasPermissionSelector(Permission.deleteAssets()),
    );
    const [retireObject, { isLoading: isRetiring }] = useRetireObjectMutation();
    const [retireError, setRetireError] = useState<string | null>(null);
    const [pendingRetire, setPendingRetire] = useState<StationObject | null>(null);

    const requestRetire = (object: StationObject) => {
        setRetireError(null);
        setPendingRetire(object);
        dispatch(setIsConfirmationDialog(true));
        dispatch(setConfirmationDialogContent({
            content: t('station_detail.objects.confirm_retire.content', {
                name: object.name ?? t('station_detail.objects.unnamed'),
                door: object.door ?? t('common.not_applicable'),
            }),
            headerText: t('station_detail.objects.confirm_retire.header'),
            leftButtonText: t('common.cancel'),
            rightButtonText: t('station_detail.objects.confirm_retire.confirm'),
        }));
    };

    const handleConfirmRetire = async () => {
        if (!pendingRetire) return;
        setRetireError(null);
        try {
            const result = await retireObject({ assetId: pendingRetire.id }).unwrap();
            invalidateInventoryReaders(dispatch);
            dispatch(setIsConfirmationDialog(false));
            setPendingRetire(null);
            // A second retire answers 200, not an error — say so rather than
            // letting the row silently not change.
            if (result?.retired === false) {
                setRetireError(t('station_detail.objects.already_retired'));
            }
        } catch (error) {
            dispatch(setIsConfirmationDialog(false));
            setPendingRetire(null);
            // The same reading as the re-link: a blocker is about THIS
            // compartment, everything else is about the request or the service.
            const read = readObjectActionFailure(readInventoryFailure(error));
            // Retire's own abort wording: the re-link's says "the run was
            // stopped", which is true of a batch of ten and nonsense for one
            // compartment.
            if (read.abort) {
                setRetireError(t(`station_detail.objects.retire_abort_${read.abort}`));
                return;
            }
            setRetireError(read.reasonKeys.length
                ? read.reasonKeys
                    .map((key) => t(`station_detail.objects.${key}`, {
                        defaultValue: t('station_detail.blockers.unknown'),
                    }))
                    .join(' ')
                : [t('station_detail.objects.retire_error'), read.detail]
                    .filter(Boolean)
                    .join(' '));
        }
    };
    const bookable = bookablePositionCount(station);
    const free = useMemo(
        () => freePositions({
            doorCount: station.doorCount,
            bufferDoorsCount: station.bufferDoorsCount,
            objects,
        }),
        [station.doorCount, station.bufferDoorsCount, objects],
    );

    /** Every bookable position, plus any object sitting outside that range. */
    const rows = useMemo((): PositionRow[] => {
        const byDoor = new Map<number, StationObject>();
        const unplaced: StationObject[] = [];
        for (const object of objects) {
            if (typeof object.door === 'number') byDoor.set(object.door, object);
            else unplaced.push(object);
        }

        const inRange: PositionRow[] = Array.from({ length: bookable }, (_, index) => ({
            door: index + 1,
            object: byDoor.get(index + 1) ?? null,
        }));

        // An object in the buffer tail, or beyond `door_count` entirely, is real
        // and must be visible — it is exactly what blocks a capacity reduction.
        const beyond: PositionRow[] = Array.from(byDoor.entries())
            .filter(([door]) => door > bookable)
            .sort(([a], [b]) => a - b)
            .map(([door, object]) => ({ door, object }));

        return [...inRange, ...beyond, ...unplaced.map((object) => ({ door: null, object }))];
    }, [objects, bookable]);

    /** How many objects each Product has here — the "configuration" answer. */
    const productSummary = useMemo(() => {
        const counts = new Map<string | null, number>();
        for (const object of objects) {
            const key = object.productId;
            counts.set(key, (counts.get(key) ?? 0) + 1);
        }
        return Array.from(counts.entries())
            .sort((a, b) => b[1] - a[1]);
    }, [objects]);

    return (
        <AssetDetailSection
            title={station.isHouse
                ? t('station_detail.sections.rooms')
                : t('station_detail.sections.objects')}
            // A house's room count is in the page header — said once.
            subtitle={station.isHouse
                ? undefined
                : t('station_detail.objects.subtitle', {
                    objects: objects.length,
                    positions: station.doorCount,
                    free: free.length,
                })}
        >
            <div className={'flex flex-col gap-4'}>
                {/* Per-product counts. Not for a house: its room list already
                    names each room's product, and the chips repeated it
                    (PO, 2026-10-19). */}
                {!station.isHouse && productSummary.length > 0 && (
                    <div className={'flex flex-wrap gap-2'}>
                        {productSummary.map(([id, objectCount]) => (
                            <span
                                key={id ?? 'unlinked'}
                                className={'rounded-full bg-foreground/5 px-3 py-1 text-sm text-foreground'}
                            >
                                {t('station_detail.objects.product_count', {
                                    product: productName(id)
                                        ?? t('station_detail.objects.unlinked'),
                                    count: objectCount,
                                })}
                            </span>
                        ))}
                    </div>
                )}

                {station.isHouse ? (
                    <HouseRoomList
                        rooms={objects}
                        productName={productName}
                    />
                ) : (
                    <>
                        <div className={'overflow-x-auto'}>
                            <table className={'w-full text-sm'}>
                                <thead>
                                    <tr className={'border-b border-foreground/5 text-left'}>
                                        <th className={'w-8 py-2 pr-2'}>
                                            <span className={'sr-only'}>
                                                {t('station_detail.objects.column_select')}
                                            </span>
                                        </th>
                                        <th className={'py-2 pr-4 font-medium text-foreground/60'}>
                                            {t('station_detail.objects.column_position')}
                                        </th>
                                        <th className={'py-2 pr-4 font-medium text-foreground/60'}>
                                            {t('station_detail.objects.column_object')}
                                        </th>
                                        <th className={'py-2 pr-4 font-medium text-foreground/60'}>
                                            {t('station_detail.objects.column_product')}
                                        </th>
                                        {hasDeletePermission && (
                                            <th className={'py-2 text-right font-medium text-foreground/60'}>
                                                {t('station_detail.objects.column_actions')}
                                            </th>
                                        )}
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((row) => (
                                        <tr
                                            key={row.object?.id ?? `free-${row.door}`}
                                            className={'border-b border-foreground/5'}
                                        >
                                            <td className={'w-8 py-2 pr-2'}>
                                                {/* Only a filled position can move — an
                                                    empty one has nothing to re-link. */}
                                                {/* A room re-links on its own page:
                                                    the API refuses `room_relink`. */}
                                                {row.object && !station.isRoomStation && (
                                                    <input
                                                        type={'checkbox'}
                                                        className={'accent-primary'}
                                                        checked={selectedIds.includes(row.object.id)}
                                                        onChange={() => toggleSelected(
                                                            (row.object as StationObject).id,
                                                        )}
                                                        /* An object with no door mapping
                                                           still has to be identifiable
                                                           here — an empty label would
                                                           read as "Platz  auswählen". */
                                                        aria-label={t(
                                                            'station_detail.objects.select_position',
                                                            {
                                                                door: row.door
                                                                    ?? row.object.name
                                                                    ?? t('station_detail.objects.unnamed'),
                                                            },
                                                        )}
                                                    />
                                                )}
                                            </td>
                                            <td className={'py-2 pr-4 text-foreground'}>
                                                {row.door ?? t('common.not_applicable')}
                                            </td>
                                            <td className={'py-2 pr-4 text-foreground'}>
                                                {row.object
                                                    ? (row.object.name
                                                        ?? t('station_detail.objects.unnamed'))
                                                    : (
                                                        <span className={'italic text-foreground/40'}>
                                                            {t('station_detail.objects.free_position')}
                                                        </span>
                                                    )}
                                            </td>
                                            <td className={'py-2 pr-4 text-foreground'}>
                                                {row.object
                                                    ? (productName(row.object.productId)
                                                        ?? t('station_detail.objects.unlinked'))
                                                    : ''}
                                            </td>
                                            {hasDeletePermission && (
                                                <td className={'py-2 text-right'}>
                                                    {/* An occupied compartment cannot be
                                                        retired — someone is holding it
                                                        right now, and removing it would
                                                        strand their booking. The API is
                                                        the enforcer; this explains the
                                                        rule earlier (ADR-0020). */}
                                                    {/* Retiring changes something, so it sits in
                                                        the row's menu (design-decisions.md ›
                                                        Zeilenaktionen in Tabellen). */}
                                                    {row.object && (
                                                        <RowActionsMenu
                                                            // Names the position the way the row's
                                                            // checkbox does ("Platz 3").
                                                            rowName={row.door != null
                                                                ? t('station_detail.objects.position_name', { door: row.door })
                                                                : (row.object.name ?? t('station_detail.objects.unnamed'))}
                                                            items={[{
                                                                id: 'retire',
                                                                label: t('station_detail.objects.retire'),
                                                                onClick: () => requestRetire(row.object as StationObject),
                                                                disabled: row.object.state?.toLowerCase() === 'inuse'
                                                                    || isRetiring,
                                                                hint: row.object.state?.toLowerCase() === 'inuse'
                                                                    ? t('station_detail.objects.retire_blocked_in_use')
                                                                    : undefined,
                                                            }]}
                                                        />
                                                    )}
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {station.bufferDoorsCount > 0 && (
                            <p className={'text-sm text-foreground/60'}>
                                {t('station_detail.objects.buffer_hint', {
                                    count: station.bufferDoorsCount,
                                })}
                            </p>
                        )}

                        {selectedObjects.length > 0 && !station.isRoomStation && (
                            <ChangeProductBar
                                selected={selectedObjects}
                                onDone={() => setSelectedIds([])}
                            />
                        )}
                    </>
                )}

                {station.isHouse && (
                    <div className={'flex flex-col gap-2'}>
                        <div>
                            <CustomButton
                                title={t('station_detail.objects.add_room')}
                                onClick={() => navigate(
                                    `${paths.createAsset}?houseId=${encodeURIComponent(station.id)}`,
                                )}
                            />
                        </div>
                    </div>
                )}
                {/* A venue or a guest apartment is ONE object that IS the
                    station, so there is nothing to add to it — another object
                    at that address is another listing, created through its own
                    page (Flow 1). Said rather than silently omitted: a missing
                    button that reads as a missing feature is the absence-shaped
                    failure. */}
                {!station.isHouse && station.isRoomStation && (
                    <p className={'text-sm text-foreground/60'}>
                        {t('station_detail.objects.room_station')}
                    </p>
                )}
                {!station.isRoomStation && (
                    <div>
                        <CustomButton
                            title={t('station_detail.objects.add_button')}
                            onClick={() => setAdding(true)}
                        />
                    </div>
                )}
            </div>

            {retireError && (
                <p className={'mt-3 text-sm text-red-500'}>{retireError}</p>
            )}

            <ConfirmationDialog
                handleClickConfirm={handleConfirmRetire}
                isLoading={isRetiring}
            />

            {!station.isRoomStation && (
                <AddObjectsDialog
                    open={adding}
                    onClose={() => setAdding(false)}
                    station={station}
                />
            )}
        </AssetDetailSection>
    );
};

export default StationObjectsSection;
